import React from 'react';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { parseComponents } from '@/ui/utils/tiles';
import { twJoin, twMerge } from 'tailwind-merge';
import { BentoNewTile } from './types/BentoNewTile';
import Container from '../../shared/Container';
import BentoBoxRenederer from './components/BentoBoxRenederer';

export default function BentoNew({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: BentoNewTile;
  trackingPrefix?: string;
}>) {
  const sameHeightRows = tile['same-height-rows']?.value;
  const lastBoxFillsEmptySpace = tile['last-box-fills-empty-space']?.value;
  const boxes = tile?.boxes?.items;
  const firstBoxContent = componentByTypename(
    boxes?.[0].components?.[0]?.content,
    'ComponentChoiceContent',
  )?.selectedComponent.content;
  const firstBoxParsedComponents = parseComponents(
    componentByTypename(firstBoxContent, 'PieceContent')?.components,
  );
  const firstBoxSpan2Columns = firstBoxParsedComponents['span-2-columns']?.value;
  const firstBoxChoiceId = componentByTypename(
    boxes?.[0].components?.[0]?.content,
    'ComponentChoiceContent',
  )?.selectedComponent.id;
  const parsedBoxes = firstBoxSpan2Columns ? boxes?.slice(1) : boxes;

  return (
    <Container>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-8">
        {sameHeightRows ? (
          // Grid
          boxes?.map((box, index) => {
            const choiceComponent = componentByTypename(
              box.components?.[0]?.content,
              'ComponentChoiceContent',
            );
            const choiceId = choiceComponent?.selectedComponent.id;
            const pieceComponent = componentByTypename(
              choiceComponent?.selectedComponent.content,
              'PieceContent',
            );
            const parsedComponents = parseComponents(pieceComponent?.components);
            const span2Columns = parsedComponents['span-2-columns']?.value;
            const rowSpan = parsedComponents['row-span']?.options?.[0]?.value;

            return (
              <div
                key={index}
                className={twJoin(
                  'relative overflow-hidden rounded-xl',
                  span2Columns && 'lg:col-span-2',
                )}
                style={{ gridRow: `span ${rowSpan}` }}
              >
                <BentoBoxRenederer
                  parsedComponents={parsedComponents}
                  choiceId={choiceId}
                  trackingPrefix={trackingPrefix}
                />
              </div>
            );
          })
        ) : (
          // Two Flex Columns
          <>
            {firstBoxSpan2Columns && (
              <div className="relative overflow-hidden rounded-xl lg:col-span-2">
                <BentoBoxRenederer
                  parsedComponents={firstBoxParsedComponents}
                  choiceId={firstBoxChoiceId}
                  trackingPrefix={trackingPrefix}
                />
              </div>
            )}
            <div className="flex flex-col gap-4 lg:gap-8">
              {parsedBoxes?.map((box, index) => {
                const isEven = index % 2 === 0;
                const isOneOfLastTwo = index >= parsedBoxes.length - 2;
                const choiceComponent = componentByTypename(
                  box.components?.[0]?.content,
                  'ComponentChoiceContent',
                );
                const choiceId = choiceComponent?.selectedComponent.id;
                const pieceComponent = componentByTypename(
                  choiceComponent?.selectedComponent.content,
                  'PieceContent',
                );
                const parsedComponents = parseComponents(pieceComponent?.components);

                return (
                  <div
                    key={index}
                    className={twMerge(
                      'relative hidden overflow-hidden rounded-xl',
                      isEven ? 'block' : 'block lg:hidden',
                      isOneOfLastTwo && lastBoxFillsEmptySpace && isEven && 'lg:flex-grow',
                    )}
                  >
                    <BentoBoxRenederer
                      parsedComponents={parsedComponents}
                      choiceId={choiceId}
                      trackingPrefix={trackingPrefix}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-4 lg:gap-8">
              {parsedBoxes?.map((box, index) => {
                const isEven = index % 2 === 0;
                const isOneOfLastTwo = index >= parsedBoxes.length - 2;
                const choiceComponent = componentByTypename(
                  box.components?.[0]?.content,
                  'ComponentChoiceContent',
                );
                const choiceId = choiceComponent?.selectedComponent.id;
                const pieceComponent = componentByTypename(
                  choiceComponent?.selectedComponent.content,
                  'PieceContent',
                );
                const parsedComponents = parseComponents(pieceComponent?.components);

                return (
                  <div
                    key={index}
                    className={twMerge(
                      'relative hidden overflow-hidden rounded-xl',
                      !isEven && 'lg:block',
                      isOneOfLastTwo && lastBoxFillsEmptySpace && !isEven && 'lg:flex-grow',
                    )}
                  >
                    <BentoBoxRenederer
                      parsedComponents={parsedComponents}
                      choiceId={choiceId}
                      trackingPrefix={trackingPrefix}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
